import type { RouteObject } from 'react-router-dom'
import { useRoutes, Navigate, useParams, useLocation } from 'react-router-dom'
import Home from '@/pages/Home'
import Jobs from '@/pages/Jobs'
import JobDetail from '@/pages/JobDetail'
import Formations from '@/pages/Formations'
import Faq from '@/pages/Faq'
import Inscription from '@/pages/Inscription'
import Dashboard from '@/pages/Dashboard'
import Backoffice from '@/pages/Backoffice'
import DashboardOrganisme from '@/pages/DashboardOrganisme'
import SeoPage from '@/pages/SeoPage'
import LegalPage from '@/pages/LegalPage'
import AdminPanel from '@/pages/AdminPanel'
import AvailableCandidates from '@/pages/AvailableCandidates'
import Messages from '@/pages/Messages'
import UsefulLinks from '@/pages/UsefulLinks'
import RedirectWithCanonical from '@/components/RedirectWithCanonical'

// Legacy redirect from /seo/:slug to the new silo-aware URLs (canonical 301 equivalent)
function LegacySeoRedirect() {
  const { slug } = useParams<{ slug: string }>()
  if (!slug) return <Navigate to="/" replace />

  // Map old (flat) slugs to new (silo) paths
  const legacyMap: Record<string, string> = {
    'emploi-bnssa': '/bnssa/emploi-bnssa',
    'formation-bnssa': '/bnssa/formation-bnssa',
    'bnssa-auto-entrepreneur': '/bnssa/travailler-avec-bnssa',
    'bnssa-interim': '/bnssa/emploi-bnssa',
    'revision-bnssa': '/bnssa/comment-devenir-bnssa',
    'bnssa-fonction-publique': '/bnssa/emploi-bnssa',
    'offre-emploi-surveillance-aquatique': '/bnssa/emploi-bnssa',
    'emploi-maitre-nageur-sauveteur': '/maitre-nageur-sauveteur/emploi',
    'bpjeps-aan': '/maitre-nageur-sauveteur/bpjeps-aan',
    'salaire-maitre-nageur': '/maitre-nageur-sauveteur/salaire',
    'offre-emploi-mns': '/maitre-nageur-sauveteur/emploi',
    'maitre-nageur-titulaire': '/maitre-nageur-sauveteur/emploi',
    'aquagym-aquabike-emploi': '/maitre-nageur-sauveteur/emploi',
  }
  const target = legacyMap[slug] ?? '/'
  return <RedirectWithCanonical to={target} />
}

// Canonical 301-equivalent redirect for legacy job-detail URL `/offre/:id` → `/offres/:id`.
// React Router's <Navigate to="/offres/:id"> does NOT substitute params, so we must read
// the param ourselves and build the target path. JobDetail will then further normalize
// the URL to the SEO-friendly slug-based canonical via its internal navigate(replace).
function LegacyJobDetailRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search, hash } = useLocation()
  if (!id) return <RedirectWithCanonical to="/offres" />
  return <RedirectWithCanonical to={`/offres/${id}${search}${hash}`} />
}

// Used in @/prerender.tsx
//
// Canonical URL strategy (301-equivalent client-side redirects to remove duplicates):
//   - /offre-emploi              → /offres                       (job listing canonical)
//   - /offre/:id                 → /offres/:id                   (then SEO slug via JobDetail)
//   - /disponibles-immediatement → /disponibles                  (CVthèque canonical)
//   - /confidentialite           → /politique-confidentialite    (legal canonical)
//   - /cgu                       → /cgv                          (legal canonical)
//   - /seo/:slug                 → /<silo>/<slug>                (legacy SEO map)
//
// NOTE: /offres/:id, /bnssa/offres-emploi/:jobSlug and /maitre-nageur-sauveteur/offres-emploi/:jobSlug
// all render <JobDetail/>, which auto-normalizes (replace) to the unique SEO canonical for the job
// and emits <link rel="canonical"> accordingly, so no additional 301 is needed there.
//
// /bnssa/emploi-bnssa vs /bnssa/offres-emploi and /formations vs /(silo)/formations are intentionally
// distinct pages (long-form guide vs annuaire / global catalog vs silo), so they are NOT redirected;
// they rely on differentiated TDK + canonicals already emitted by their pages.
export const routes: RouteObject[] = [
  { path: '/', element: <Home /> },
  { path: '/offres', element: <Jobs /> },
  // 301 → /offres (canonical job listing URL)
  { path: '/offre-emploi', element: <RedirectWithCanonical to="/offres" /> },
  { path: '/offres/:id', element: <JobDetail /> },
  // 301 → /offres/:id (param-aware redirect; JobDetail then re-normalizes to SEO slug)
  { path: '/offre/:id', element: <LegacyJobDetailRedirect /> },
  { path: '/formations', element: <Formations /> },
  { path: '/faq', element: <Faq /> },
  { path: '/inscription', element: <Inscription /> },
  { path: '/connexion', element: <Inscription /> },
  { path: '/dashboard', element: <Dashboard /> },
  { path: '/backoffice', element: <Backoffice /> },
  { path: '/dashboard-organisme', element: <DashboardOrganisme /> },
  { path: '/admin', element: <AdminPanel /> },
  { path: '/disponibles', element: <AvailableCandidates /> },
  // Pages CVthèque indexables filtrées par qualification (+ région) :
  //   /disponibles/bnssa            → tous les BNSSA disponibles
  //   /disponibles/bnssa/bretagne   → BNSSA disponibles en Bretagne
  { path: '/disponibles/:qualifSlug', element: <AvailableCandidates /> },
  { path: '/disponibles/:qualifSlug/:regionSlug', element: <AvailableCandidates /> },
  // 301 → /disponibles (canonical CVthèque URL)
  { path: '/disponibles-immediatement', element: <RedirectWithCanonical to="/disponibles" /> },

  // Messagerie interne
  { path: '/messages', element: <Messages /> },

  // === BNSSA silo routes (URL rewriting vers Jobs / Formations / SeoPage) ===
  { path: '/bnssa', element: <SeoPage /> },
  { path: '/bnssa/offres-emploi', element: <Jobs /> },
  // Fiche offre BNSSA SEO-friendly : /bnssa/offres-emploi/<title>-<ville>-<dep>-<id>
  { path: '/bnssa/offres-emploi/:jobSlug', element: <JobDetail /> },
  { path: '/bnssa/formations', element: <Formations /> },
  { path: '/bnssa/articles', element: <SeoPage /> },
  { path: '/bnssa/:childSlug', element: <SeoPage /> },

  // === MNS silo routes (URL rewriting vers Jobs / Formations / SeoPage) ===
  { path: '/maitre-nageur-sauveteur', element: <SeoPage /> },
  { path: '/maitre-nageur-sauveteur/offres-emploi', element: <Jobs /> },
  // Fiche offre MNS SEO-friendly : /maitre-nageur-sauveteur/offres-emploi/<title>-<ville>-<dep>-<id>
  { path: '/maitre-nageur-sauveteur/offres-emploi/:jobSlug', element: <JobDetail /> },
  { path: '/maitre-nageur-sauveteur/formations', element: <Formations /> },
  { path: '/maitre-nageur-sauveteur/articles', element: <SeoPage /> },
  { path: '/maitre-nageur-sauveteur/:childSlug', element: <SeoPage /> },

  // Legacy redirect (anciennes URLs /seo/:slug)
  { path: '/seo/:slug', element: <LegacySeoRedirect /> },

  // Legal pages
  { path: '/mentions-legales', element: <LegalPage type="mentions-legales" /> },
  // 301 → /politique-confidentialite (canonical privacy URL)
  { path: '/confidentialite', element: <RedirectWithCanonical to="/politique-confidentialite" /> },
  { path: '/politique-confidentialite', element: <LegalPage type="politique-confidentialite" /> },
  // 301 → /cgv (canonical terms URL)
  { path: '/cgu', element: <RedirectWithCanonical to="/cgv" /> },
  { path: '/cgv', element: <LegalPage type="cgv" /> },

  // Page liens utiles (backlinks SEO), accessible depuis le footer
  { path: '/liens-utiles', element: <UsefulLinks /> },

  { path: '*', element: <Home /> },
]

function App() {
  return useRoutes(routes)
}

export default App
